import React from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { useUser } from '@clerk/clerk-react'
import TestPage from './TestPage'
import HomePage from './pages/HomePage'
import DevelopmentHomePage from './pages/DevelopmentHomePage'
import ConversationPage from './pages/ConversationPage'
import CyranoEditor from './pages/CyranoEditor'
import FamilyDashboard from './pages/FamilyDashboard'
import SignIn from './pages/SignIn'
import SignUp from './pages/SignUp'
import SignOut from './pages/SignOut'
import Dashboard from './pages/Dashboard'
import Onboarding from './pages/Onboarding'
import CustomCheckout from './pages/CustomCheckout'
import PaymentSuccess from './pages/PaymentSuccess'
import CollaborationDashboard from './pages/CollaborationDashboard'
import NewProject from './pages/NewProject'
import PhotoGallery from './pages/PhotoGallery'
import AcceptWriterInvite from './pages/AcceptWriterInvite'
import ManageWriters from './pages/ManageWriters'
import FuneralGuide from './pages/FuneralGuide'
import Layout from './components/Layout'
// import { useAuthStore } from './store/authStore' // Not currently used
import { SupabaseProvider } from './contexts/SupabaseContext'

// Wrapper to force CyranoEditor remount when eulogyId changes
function CyranoEditorKeyed() {
  const { eulogyId } = useParams<{ eulogyId: string }>();
  return <CyranoEditor key={eulogyId} />;
}

function App() {
  const { isLoaded, isSignedIn } = useUser()
  const [loadTimeout, setLoadTimeout] = React.useState(false)
  const location = useLocation()

  // If Clerk doesn't load within 10s, show fallback so user isn't stuck
  React.useEffect(() => {
    if (isLoaded) return
    const t = setTimeout(() => setLoadTimeout(true), 10000)
    return () => clearTimeout(t)
  }, [isLoaded])

  // When Clerk fails and user is at root, send them to static landing so the site still loads
  React.useEffect(() => {
    if (!isLoaded && loadTimeout && location.pathname === '/') {
      window.location.replace('/landing.html')
      return
    }
  }, [isLoaded, loadTimeout, location.pathname])

  // Show loading spinner while Clerk loads
  if (!isLoaded && !loadTimeout) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-warm-background">
        <div className="loading-pulse">
          <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16" />
        </div>
      </div>
    )
  }

  // Fallback when Clerk is slow or fails to load (no bypass: users must sign in and pay)
  if (!isLoaded && loadTimeout) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-warm-background px-4 text-center">
        <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16 mb-6 opacity-80" />
        <h1 className="text-xl font-semibold text-space-cadet mb-2">Having trouble loading?</h1>
        <p className="text-cool-gray mb-6 max-w-sm">
          The sign-in service is taking longer than usual. You can still browse the site, or try again.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
          <a
            href="/landing.html"
            className="px-4 py-2.5 bg-space-cadet text-white rounded-lg font-medium hover:bg-space-cadet/90 transition-colors no-underline"
          >
            View landing page
          </a>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2.5 border border-space-cadet/30 text-space-cadet rounded-lg font-medium hover:bg-space-cadet/5 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  // Use development homepage in development mode
  const isDevelopment = process.env.NODE_ENV === 'development' ||
                       window.location.hostname.includes('vercel.app')

  return (
    <SupabaseProvider>
      <AnimatePresence mode="wait">
        <Routes>
          {/* Authentication Routes (no layout) */}
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/sign-out" element={<SignOut />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/accept-writer-invite" element={<AcceptWriterInvite />} />
          <Route path="/manage-writers/:licenseId" element={<ManageWriters />} />
          <Route path="/funeral-guide" element={<FuneralGuide />} />
          <Route path="/funeral-guide/:licenseId" element={<FuneralGuide />} />
          <Route path="/new-project" element={<NewProject />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/checkout" element={<CustomCheckout />} />
          <Route
            path="/photos/:testamentLicenseId"
            element={
              isSignedIn ? (
                <PhotoGallery />
              ) : (
                <Navigate to="/sign-in" replace />
              )
            }
          />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route
            path="/collaboration/:sessionId"
            element={
              isSignedIn ? (
                <CollaborationDashboard />
              ) : (
                <Navigate to="/sign-in" replace />
              )
            }
          />

          {/* Main App Routes (with layout) */}
          <Route path="/" element={<Layout />}>
            <Route index element={<ExistingInterface />} />
            <Route path="test" element={<TestPage />} />
            <Route path="react-homepage" element={<HomePage />} />
            <Route path="original" element={isDevelopment ? <DevelopmentHomePage /> : <HomePage />} />
            <Route path="production" element={<HomePage />} />
            <Route
              path="conversation/:sessionId?"
              element={
                isSignedIn ? (
                  <ConversationPage />
                ) : (
                  <Navigate to="/sign-in" replace />
                )
              }
            />
            {/* /create-eulogy was the old React-based EulogyConversation page,
                superseded by the static conversation-interface.html flow.
                Removed 2026-05-03 — redirects to /new-project (the canonical
                "start a new piece" entry point) so any stale bookmarks land
                somewhere useful instead of 404. */}
            <Route path="create-eulogy" element={<Navigate to="/new-project" replace />} />
            <Route
              path="eulogy/:eulogyId"
              element={
                isSignedIn ? (
                  <CyranoEditorKeyed />
                ) : (
                  <Navigate to="/sign-in" replace />
                )
              }
            />
            <Route
              path="editor"
              element={
                isSignedIn ? (
                  <CyranoEditor />
                ) : (
                  <Navigate to="/sign-in" replace />
                )
              }
            />
            <Route
              path="family/:groupId"
              element={
                isSignedIn ? (
                  <FamilyDashboard />
                ) : (
                  <Navigate to="/sign-in" replace />
                )
              }
            />
            <Route path="existing" element={<ExistingInterface />} />
          </Route>
        </Routes>
      </AnimatePresence>
    </SupabaseProvider>
  )
}

// Bridge to existing HTML interface for backward compatibility
function ExistingInterface() {
  const { isLoaded, isSignedIn } = useUser()

  React.useEffect(() => {
    if (!isLoaded) return

    if (!isSignedIn) {
      window.location.href = '/landing.html'
      return
    }

    // Signed-in users always go to dashboard
    window.location.href = '/dashboard'
  }, [isLoaded, isSignedIn])

  return (
    <div className="flex items-center justify-center min-h-screen bg-warm-background">
      <div className="text-center">
        <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16 mx-auto animate-pulse" />
        <p className="text-cool-gray mt-4">Loading...</p>
      </div>
    </div>
  )
}

export default App